import { motion } from 'framer-motion';
import {
  Droplets,
  Users,
  DollarSign,
  TrendingUp,
  Fuel,
  Clock,
  ArrowRight,
  Car,
} from 'lucide-react';
import { Link } from 'react-router-dom';

// Sample data
const WASH_STATS = {
  todayWashes: 24,
  memberWashes: 18,
  singleWashes: 6,
  singleRevenue: 127,
  activeMembers: 156,
  monthlyRevenue: 5842,
};

const GAS_PRICES = [
  { grade: '87', label: 'Regular', price: 2.899 },
  { grade: '89', label: 'Midgrade', price: 2.999 },
  { grade: '91', label: 'Premium', price: 3.199 },
];

const RECENT_WASHES = [
  { time: '10:32 AM', name: 'Sarah Johnson', plan: 'Platinum', vehicle: 'Subaru Outback', bay: 1, type: 'member' },
  { time: '10:15 AM', name: 'Walk-in', plan: 'Single Xpress', vehicle: 'Grey Sedan', bay: 2, type: 'single' },
  { time: '9:45 AM', name: 'Mike Chen', plan: 'Everything', vehicle: 'Tesla Model 3', bay: 1, type: 'member' },
  { time: '9:12 AM', name: 'Walk-in', plan: 'Single Platinum', vehicle: 'White SUV', bay: 1, type: 'single' },
  { time: '8:30 AM', name: 'Lisa Rodriguez', plan: 'Plus Wheels', vehicle: 'Honda CR-V', bay: 2, type: 'member' },
  { time: '8:05 AM', name: 'Tom Anderson', plan: 'Platinum', vehicle: 'Ford F-150', bay: 1, type: 'member' },
];

function StatCard({ icon: Icon, label, value, sub, color, delay }: {
  icon: typeof Droplets;
  label: string;
  value: string | number;
  sub?: string;
  color: string;
  delay: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="card"
    >
      <div className="card-body flex items-start justify-between">
        <div>
          <p className="text-text-muted text-xs font-mono uppercase tracking-wide">{label}</p>
          <p className={`text-3xl font-bold mt-1 ${color}`}>{value}</p>
          {sub && <p className="text-text-muted text-xs mt-1">{sub}</p>}
        </div>
        <div className={`p-2 rounded ${color === 'text-jrd-orange' ? 'bg-jrd-orange/10' : color === 'text-jrd-green' ? 'bg-jrd-green/10' : 'bg-text-muted/10'}`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      </div>
    </motion.div>
  );
}

export function Dashboard() {
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-text-primary">Dashboard</h2>
        <p className="text-text-muted text-sm font-mono">
          <span className="text-jrd-orange">//</span> Today's operations overview
          <span className="ml-3 inline-flex items-center gap-1 px-2 py-0.5 bg-jrd-green/10 border border-jrd-green/30 rounded text-jrd-green text-[10px]">
            SAMPLE DATA
          </span>
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Droplets} label="Today's Washes" value={WASH_STATS.todayWashes} sub={`${WASH_STATS.memberWashes} member / ${WASH_STATS.singleWashes} single`} color="text-jrd-orange" delay={0} />
        <StatCard icon={Users} label="Active Members" value={WASH_STATS.activeMembers} sub="Unlimited wash plans" color="text-jrd-green" delay={0.05} />
        <StatCard icon={DollarSign} label="Today's Revenue" value={`$${WASH_STATS.singleRevenue}`} sub="Single wash sales" color="text-text-primary" delay={0.1} />
        <StatCard icon={TrendingUp} label="Monthly Revenue" value={`$${WASH_STATS.monthlyRevenue.toLocaleString()}`} sub="Membership + singles" color="text-jrd-green" delay={0.15} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Wash Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card lg:col-span-2"
        >
          <div className="card-header flex items-center justify-between">
            <span>// RECENT WASHES</span>
            <Link to="/car-wash" className="text-jrd-orange hover:underline text-xs flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="card-body p-0">
            <div className="divide-y divide-text-muted/10">
              {RECENT_WASHES.map((wash, idx) => (
                <div key={idx} className="px-4 py-3 flex items-center justify-between hover:bg-bg-card-hover transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      wash.type === 'member' ? 'bg-jrd-green/10' : 'bg-jrd-orange/10'
                    }`}>
                      {wash.type === 'member' ? (
                        <Users className="w-4 h-4 text-jrd-green" />
                      ) : (
                        <Car className="w-4 h-4 text-jrd-orange" />
                      )}
                    </div>
                    <div>
                      <p className="text-text-primary text-sm">{wash.name}</p>
                      <p className="text-text-muted text-xs">{wash.vehicle}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-medium ${
                      wash.type === 'member' ? 'text-jrd-green' : 'text-jrd-orange'
                    }`}>
                      {wash.plan}
                    </p>
                    <p className="text-text-muted text-xs flex items-center gap-1 justify-end">
                      <Clock className="w-3 h-3" /> {wash.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Gas Prices Widget */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="card"
        >
          <div className="card-header flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Fuel className="w-4 h-4 text-jrd-orange" />
              // GAS PRICES
            </span>
            <Link to="/gas-prices" className="text-jrd-orange hover:underline text-xs flex items-center gap-1">
              Details <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="card-body space-y-3">
            <p className="text-text-muted text-xs">IN N' OUT Market - 743 N Century Ave</p>
            {GAS_PRICES.map((gas) => (
              <div key={gas.grade} className="flex items-center justify-between p-3 bg-bg-input rounded border border-text-muted/20">
                <div>
                  <span className="text-text-muted text-xs font-mono">{gas.grade}</span>
                  <span className="text-text-secondary text-xs ml-2">{gas.label}</span>
                </div>
                <span className="text-xl font-bold text-text-primary font-mono lcd-display">
                  ${gas.price.toFixed(3)}<sup className="text-xs">9</sup>
                </span>
              </div>
            ))}
            <div className="text-center">
              <p className="text-text-muted text-[10px] font-mono mt-2">
                Updated just now
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
